import { Col, Flex } from "antd";
import { observer } from "mobx-react-lite";
import {
  OPERATION_TYPE,
  BID_REQUEST_STATUS_ENUM,
  BID_REQUEST_TYPE_ENUM,
  BidWalletCheckGridDto,
  QuoteAcceptData,
  QUOTE_SELECTION_ENUM,
  RISK_STATUS_ENUM,
  NOTIFICATION_TYPE_ENUM,
  AML_REQUEST_ACTION,
  CONTRACT_DIRECTION_ENUM,
} from "interfaces";
import { useStores } from "stores";
import { CustomButton } from "ui";
import { checkDaysPassed, isStatusIncluded, notificationPeriodDays, parseErrorMessage } from "utils";
import { RelevantOperationType } from "services";
import { BidOperationModal } from "./BidOperationModal";

import { PassAmlButton } from "./PassAml";
import { ExecuteBidBlock } from "./ExecuteBid";
import { ApproveBidButton } from "./ApproveBid";

import { AcceptConditionsButton } from "./AcceptConditions";

import { DIRECT_PAYMENT_CUSTOMER_LP, QUOTE_SELECTION, SAFE_DEAL } from "utils/envConstants";
import { AcceptRubButton } from "./AcceptRub";
import { ReceivedRubButton } from "./ReceivedRubButton";
import { ActivateBidButton } from "./ActivateBid";
import { ButtonReturnToEditing } from "../ButtonReturnToEditing";
import { OfferQuoteButton } from "../OfferQuote/OfferQuoteButton";
import { GetCollectQuoteButton } from "../Quotes/GetCollectQuote/GetCollectQuote";

import { WithdrawalNominalFundsBtn } from "./WithdrawalNominalFundsBtn";
import { CustomerExecutionBtn } from "./CustomerExecutionBtn";

import { RetryTransactionBtn } from "./RetryTransactionBtn/RetryTransactionBtn";
import { ApproveDoubleVerificationBtn } from "./ApproveDoubleVerificationBtn";
import { DeclineDraftBidBtn, ReturnToDraftBidBtn } from "./BidDraftActionBtn";

interface IBidOperationButtonProps {
  btnTitle: string;
  operationType: RelevantOperationType;
  loading?: boolean;
  disabled?: boolean;
  messageTitle: string;
  tooltip: string;
  id?: string;
  quoteRequestData?: QuoteAcceptData;
  modalTitle: string;
  btnTooltip?: string;
}

export const BidOperationButton = observer((props: IBidOperationButtonProps) => {
  const { actionModals } = useStores();

  const onClickHandler = () => {
    actionModals.open(() => <BidOperationModal {...props} />, {
      width: "700px",
      title: props.modalTitle,
      noValidation: true,
      isOverlayClose: false,
      onApply: () => {
        return Promise.resolve();
      },
    });
  };

  return (
    <Col>
      <CustomButton
        id={props.id}
        onClick={onClickHandler}
        disabled={props.disabled}
        loading={props.loading}
        tooltip={props.btnTooltip}
      >
        {props.btnTitle}
      </CustomButton>
    </Col>
  );
});

interface IProps {
  validWalletsChecks: {
    isValid: boolean;
    hasApprovalNeeded: boolean;
    lastCheckTime: Date | null;
    walletChecks: BidWalletCheckGridDto[];
  };
  onWalletChecksUpdated: () => Promise<void>;
  visibleButtons: {
    showActivateBidButton: boolean;
    showExecuteBidButtons: boolean;
  };
  id: string;
}

export const BidOperations = observer((props: IProps) => {
  const { user, bidCard, bidHistory } = useStores();

  const { status, type, quote, contract, requestProcessing } = bidCard.bid;

  const {
    isRiskManager,
    isSeniorOperator,
    isJuniorOperator,
    isSeniorCustomer,
    isJuniorCustomer,
    isSeniorLP,
    isJuniorLP,
    isCustomer,
    isProvider,
    isOperator,
  } = user.roles;

  const showPassAmlButton = isRiskManager && status === BID_REQUEST_STATUS_ENUM.AML_FAILED;

  const hasTokenBalance = Number(bidCard?.bid?.balance?.token) > 0;
  const enoughToken = (Number(bidCard?.bid?.balance?.token) ?? 0) >= (bidCard.bid.amount ?? 0);

  const amlCheckQuote = bidCard.amlCheckStatusData?.some(
    (i) => i.requestAction === AML_REQUEST_ACTION.ACCEPT_QUOTE && i.checksCompleted && i.checksSuccess
  );

  const isExp = bidCard.bid.contract.direction === CONTRACT_DIRECTION_ENUM.EXP;
  const isImp = bidCard.bid.contract.direction === CONTRACT_DIRECTION_ENUM.IMP;

  // const showWithdrawalNominalFundsBtn =
  //   isProvider &&
  //   isExp &&
  //   isStatusIncluded(status, [BID_REQUEST_STATUS_ENUM.FUNDING]) &&
  //   (bidCard.bid.rubBalance ?? 0) < (bidCard.bid.amountRub ?? 0) &&
  //   !DIRECT_PAYMENT_CUSTOMER_LP &&
  //   QUOTE_SELECTION === QUOTE_SELECTION_ENUM.CUSTOMER;

  const isShowBankAccount =
    !DIRECT_PAYMENT_CUSTOMER_LP &&
    bidCard.bid.status === "FUNDING" &&
    (((isCustomer || (isSeniorOperator && checkDaysPassed(bidCard.bid.created, notificationPeriodDays).isOverDays)) &&
      isExp) ||
      ((isSeniorLP || isJuniorLP) && isImp));

  const showApproveButton =
    (isSeniorOperator || isJuniorOperator) &&
    status === BID_REQUEST_STATUS_ENUM.DRAFT &&
    type === BID_REQUEST_TYPE_ENUM.REQUEST_WITH_AGENT;
  const showDeclineDraftButton = isSeniorOperator && status === BID_REQUEST_STATUS_ENUM.DRAFT;
  const showReturnToDraftButton =
    (isJuniorCustomer || isSeniorCustomer) && status === BID_REQUEST_STATUS_ENUM.DRAFT_DECLINED;

  const showAcceptConditionsButton =
    (isJuniorCustomer || isSeniorCustomer) && status === BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL;

  const showAcceptButton =
    (isJuniorCustomer || isSeniorCustomer) && status === BID_REQUEST_STATUS_ENUM.QUOTE_PROPOSED && quote;

  //TODO: DIRECT_PAYMENT - временная заглушка для нового процесса
  //потом пересмотреть логику с 134 по 154 строку
  // const showExportTransferSentButton =
  //   (isSeniorLP || isJuniorLP) &&
  //   status === BID_REQUEST_STATUS_ENUM.FUNDING &&
  //   !requestProcessing.fiatSent &&
  //   contract.direction === "EXP" &&
  //   quote &&
  //   DIRECT_PAYMENT_CUSTOMER_LP;

  const changeTitleTransferSentButton =
    (isJuniorCustomer || isSeniorCustomer) &&
    status === BID_REQUEST_STATUS_ENUM.FUNDING &&
    contract.direction === "IMP" &&
    !requestProcessing.fiatSent &&
    quote &&
    DIRECT_PAYMENT_CUSTOMER_LP;

  const checkProviderToImp =
    (isSeniorLP || isJuniorLP) &&
    status === BID_REQUEST_STATUS_ENUM.FUNDING &&
    requestProcessing.fiatSent &&
    !requestProcessing.fiatReceived &&
    contract.direction === "IMP" &&
    quote;

  const checkCustomerToExp =
    (isSeniorCustomer || isJuniorCustomer) &&
    status === BID_REQUEST_STATUS_ENUM.FUNDING &&
    requestProcessing.fiatSent &&
    !requestProcessing.fiatReceived &&
    contract.direction === "EXP" &&
    quote;

  const showImportTransferReceivedButton = checkProviderToImp && DIRECT_PAYMENT_CUSTOMER_LP;
  const showImportTransferSentButtonDp = checkProviderToImp && DIRECT_PAYMENT_CUSTOMER_LP;

  const showExportTransferReceivedButton = checkCustomerToExp && DIRECT_PAYMENT_CUSTOMER_LP;
  const showExportTransferSentButtonDp = checkCustomerToExp && DIRECT_PAYMENT_CUSTOMER_LP;

  const checkSeniorOperator = (operationType: string) => {
    return (
      checkDaysPassed(bidCard.bid.created, notificationPeriodDays).isOverDays &&
      isStatusIncluded(status, [operationType]) &&
      isExp &&
      isSeniorOperator
    );
  };

  const showApproveButtonSeniorOperator =
    checkSeniorOperator(BID_REQUEST_STATUS_ENUM.QUOTE_PROPOSED) && QUOTE_SELECTION_ENUM.OPERATOR === QUOTE_SELECTION;

  const showReceivedButtonSeniorOperatorPrevProcess =
    isExp &&
    DIRECT_PAYMENT_CUSTOMER_LP &&
    checkSeniorOperator(BID_REQUEST_STATUS_ENUM.FUNDING) &&
    QUOTE_SELECTION_ENUM.OPERATOR === QUOTE_SELECTION &&
    hasTokenBalance &&
    !bidCard.bid.requestProcessing.fiatReceived &&
    bidCard.bid.requestProcessing.fiatSent;

  const showReceivedButtonSeniorOperatorNewProcess =
    checkSeniorOperator(BID_REQUEST_STATUS_ENUM.FUNDING) && isExp && !DIRECT_PAYMENT_CUSTOMER_LP && !SAFE_DEAL;

  const showReceivedBtn =
    showExportTransferReceivedButton || showImportTransferReceivedButton || showReceivedButtonSeniorOperatorPrevProcess;

  //Убрали в 3.2.0 для старого процесса (fiat-sent)
  const showTransferSentBtnImp =
    status === BID_REQUEST_STATUS_ENUM.FUNDING &&
    contract.direction === "IMP" &&
    !requestProcessing.fiatSent &&
    quote &&
    DIRECT_PAYMENT_CUSTOMER_LP &&
    (isJuniorCustomer || isSeniorCustomer);

  const showTransferSentBtnExp =
    status === BID_REQUEST_STATUS_ENUM.FUNDING &&
    contract.direction === "EXP" &&
    !requestProcessing.fiatSent &&
    quote &&
    DIRECT_PAYMENT_CUSTOMER_LP &&
    (isJuniorLP || isSeniorLP);

  const showSentBtn = showTransferSentBtnImp || showTransferSentBtnExp;

  // const showAcceptRubButton =
  //   !DIRECT_PAYMENT_CUSTOMER_LP &&
  //   (!bidCard.bid.requestProcessing.rubReceivedByPlatform || !bidCard.bid.requestProcessing.feeRubReceivedByPlatform) &&
  //   (isJuniorOperator || isSeniorOperator) &&
  //   isStatusIncluded(status, [BID_REQUEST_STATUS_ENUM.FUNDING]) &&
  //   !SAFE_DEAL;

  const showReceivedRubButton =
    DIRECT_PAYMENT_CUSTOMER_LP &&
    (isJuniorOperator || isSeniorOperator) &&
    isStatusIncluded(status, [BID_REQUEST_STATUS_ENUM.EXECUTION]) &&
    bidCard.bid.requestProcessing.fiatWithdrawalRequested;

  const changeTitleSentBtnPl = (isSeniorLP || isJuniorLP) && isExp && !DIRECT_PAYMENT_CUSTOMER_LP;
  const changeTitleSentBtnCustomer = isCustomer && isExp && !DIRECT_PAYMENT_CUSTOMER_LP;

  const changeTitleReceivedBtnPl = (isSeniorLP || isJuniorLP) && isImp && !DIRECT_PAYMENT_CUSTOMER_LP;

  function hasStatusHistory(type: string) {
    return bidHistory.statusHistory.some((i) => isStatusIncluded(i.status, [type]));
  }

  const hasHistoryStatusAgentProposalAccepted = hasStatusHistory(BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL_ACCEPTED);
  const hasHistoryStatusNew = hasStatusHistory(BID_REQUEST_STATUS_ENUM.NEW);

  const isOperatorDraftApproved =
    (isJuniorOperator || isSeniorOperator) &&
    isStatusIncluded(status, [BID_REQUEST_STATUS_ENUM.DRAFT_APPROVED]) &&
    !(hasHistoryStatusAgentProposalAccepted || hasHistoryStatusNew);

  const isCustomerDraftApproved = isCustomer && isStatusIncluded(status, [BID_REQUEST_STATUS_ENUM.DRAFT_APPROVED]);

  const showButtonSeniorOperator =
    checkDaysPassed(bidCard.bid.created, notificationPeriodDays).isOverDays &&
    isStatusIncluded(status, [BID_REQUEST_STATUS_ENUM.DRAFT_APPROVED]) &&
    isExp &&
    isSeniorOperator &&
    Number(bidCard.bid.balance.token) > 0;

  const isCustomerBidFundedImp = isStatusIncluded(status, [BID_REQUEST_STATUS_ENUM.FUNDED]) && isImp;

  const disabled =
    bidCard?.bid?.contract?.direction !== "IMP"
      ? !(amlCheckQuote && enoughToken && bidCard?.bid?.contract?.direction === "EXP")
      : false;

  const showExecuteBidBlock = isStatusIncluded(status, [BID_REQUEST_STATUS_ENUM.FUNDED]);
  const canExecuteBidNewProcess =
    (showExecuteBidBlock && isImp && bidCard.bid.requestProcessing.customerExecuted && !DIRECT_PAYMENT_CUSTOMER_LP) ||
    (isExp && showExecuteBidBlock);

  const canExecuteBidOldProcess = showExecuteBidBlock && (isExp || isImp) && DIRECT_PAYMENT_CUSTOMER_LP;
  const canExecuteBid = canExecuteBidNewProcess || canExecuteBidOldProcess;

  const showApproveBidInfoButtonCustomer =
    isCustomer &&
    isImp &&
    !requestProcessing.destinationWalletVerified &&
    user.userInfo?.id !== bidCard.bid.userId &&
    isStatusIncluded(status, [BID_REQUEST_STATUS_ENUM.DRAFT]);

  const showRetryTransactionBtn =
    bidCard.bid.requestProcessing.outgoingTxFailed &&
    isStatusIncluded(status, [BID_REQUEST_STATUS_ENUM.EXECUTION]) &&
    isOperator;

  return (
    <Flex vertical gap="middle">
      <Flex gap="middle" wrap="wrap" align="center">
        {showPassAmlButton && <PassAmlButton />}
        {(showAcceptButton || (showApproveButtonSeniorOperator && hasTokenBalance)) && (
          <BidOperationButton
            id="request-approve-button"
            operationType={OPERATION_TYPE.REQUEST_APPROVE}
            disabled={disabled}
            btnTitle="Принять условия"
            modalTitle="Принять условия"
            messageTitle={`Принимаю условия на${" "}
            ${contract.direction === "EXP" ? "продажу " : "покупку "}
            цифровой валюты со следующими параметрами:`}
            tooltip={
              !DIRECT_PAYMENT_CUSTOMER_LP
                ? "Данное действие является Юридически значимым"
                : "Данное действие является подписанием Договора купли-продажи ЦВ и является юридически значимым"
            }
            btnTooltip={
              disabled ? "Нельзя принять котировку до поступления криптовалюты и окончания всех проверок" : undefined
            }
          />
        )}
        {showSentBtn && !isShowBankAccount && (
          <BidOperationButton
            id="bid-sent-transfer"
            operationType={OPERATION_TYPE.BANK_TRANSFER_SENT}
            btnTitle={
              changeTitleTransferSentButton
                ? "Рубли отправлены, подписать платёжное поручение"
                : changeTitleSentBtnPl
                  ? "Рубли отправил, перевести ЦВ"
                  : showExportTransferSentButtonDp || showImportTransferSentButtonDp
                    ? "Подпишите платёжное поручение для получения оплаты за ЦВ"
                    : "Рубли отправлены, подписать платёжное поручение"
            }
            modalTitle={
              changeTitleTransferSentButton
                ? "Подписать поручение на выплату ЦВ"
                : changeTitleSentBtnPl
                  ? "Подписать поручение на вывод цифровой валюты"
                  : showExportTransferSentButtonDp || showImportTransferSentButtonDp
                    ? "Подпишите платёжное поручение для получения оплаты за ЦВ"
                    : "Средства переведены, подписать поручение на списание ЦВ"
            }
            messageTitle={
              changeTitleTransferSentButton
                ? "Платежное поручение на выплату цифровой валюты в адрес иностранного получателя в исполнение заявки со следующими параметрами:"
                : changeTitleSentBtnPl
                  ? "Платежное поручение на вывод цифровой валюты со следующими параметрами"
                  : "Платежное поручение на перевод средств в исполнение ДКП цифровой валюты со следующими параметрами:"
            }
            tooltip="Действие является Юридически Значимым"
          />
        )}
        {showReceivedBtn && (
          <BidOperationButton
            operationType={OPERATION_TYPE.BANK_TRANSFER_RECEIVED}
            btnTitle="Рубли получены, подписать платёжное поручение"
            modalTitle="Средства получены, подписать поручение на списание ЦВ"
            messageTitle="Платежное поручение на перевод средств в исполнение ДКП цифровой валюты со следующими параметрами:"
            tooltip="Действие является Юридически Значимым"
          />
        )}
        {(isShowBankAccount || showReceivedButtonSeniorOperatorNewProcess) &&
          !bidCard.bid.requestProcessing.fiatReceived &&
          bidCard.bid.requestProcessing.rubReceivedByPlatform && (
            <BidOperationButton
              operationType={OPERATION_TYPE.BANK_TRANSFER_RECEIVED}
              btnTitle={
                changeTitleSentBtnCustomer
                  ? "Вывести средства с р/с платформы"
                  : `Подписать платежное поручение на ${changeTitleReceivedBtnPl ? "вывод" : "списание "} средств с р/с Платформы`
              }
              modalTitle={
                changeTitleSentBtnCustomer
                  ? "Подписать поручение на вывод средств с р/с платформы"
                  : `Подписать ${!changeTitleReceivedBtnPl ? "платежное" : ""} поручение на ${changeTitleReceivedBtnPl ? "вывод" : "списание "} средств с р/с Платформы`
              }
              messageTitle={
                changeTitleSentBtnCustomer
                  ? "Поручение на вывод средств с р/с платформы в исполнение заявки со следующими параметрами"
                  : changeTitleReceivedBtnPl
                    ? "Платежное поручение на перевод цифровой валюты в адрес получателя и вывод средств с номинального счета платформы"
                    : `Платежное поручение на перевод средств в исполнение ДКП цифровой валюты со следующими параметрами:`
              }
              tooltip="Действие является Юридически Значимым"
            />
          )}

        {isOperatorDraftApproved && <ButtonReturnToEditing id={props.id} />}
        {(isSeniorLP || isJuniorLP) && status === BID_REQUEST_STATUS_ENUM.NEW && <OfferQuoteButton />}
        {(isCustomerDraftApproved || showButtonSeniorOperator) && <GetCollectQuoteButton />}
        {showReceivedRubButton && <ReceivedRubButton />}
        {/* {showAcceptRubButton && <AcceptRubButton />} */}
        {showApproveBidInfoButtonCustomer && (
          <ApproveDoubleVerificationBtn
            operationPath={OPERATION_TYPE.VERIFY_WALLET_REQUEST}
            walletAddress={bidCard.bid?.destinationWallet}
          />
        )}
        {props.visibleButtons.showExecuteBidButtons && (isJuniorOperator || isSeniorOperator) && canExecuteBid && (
          <ExecuteBidBlock
            validWalletsChecks={props.validWalletsChecks}
            onWalletChecksUpdated={props.onWalletChecksUpdated}
          />
        )}
        {showAcceptConditionsButton && <AcceptConditionsButton />}
        {showApproveButton && <ApproveBidButton />}
        {showDeclineDraftButton && <DeclineDraftBidBtn />}
        {showReturnToDraftButton && <ReturnToDraftBidBtn />}
        {props.visibleButtons.showActivateBidButton &&
          (isExp || (isImp && requestProcessing.destinationWalletVerified)) && (
            <ActivateBidButton walletChecks={props.validWalletsChecks.walletChecks} />
          )}
        {/* {showWithdrawalNominalFundsBtn && <WithdrawalNominalFundsBtn />} */}
        {isCustomerBidFundedImp &&
          !DIRECT_PAYMENT_CUSTOMER_LP &&
          isCustomer &&
          !bidCard.bid.requestProcessing.customerExecuted && <CustomerExecutionBtn />}
        {showRetryTransactionBtn && <RetryTransactionBtn />}
      </Flex>
    </Flex>
  );
});
